import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { ReviewNotificationJobContract } from '@/contracts/customers'
import { useAuth } from '@/lib/auth'
import { listReviewNotificationJobs, retryReviewNotificationJob } from '@/lib/data-client/customers'
import { useBusinessWorkflowDataClientHydration } from '@/lib/data-client/use-business-workflow-hydration'
import { hasFormAdminAccess } from '@/lib/permissions'
import { BellRing, Loader2, RotateCcw, Search, Send } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

type ReviewNotificationListStatus =
  | ReviewNotificationJobContract['status']
  | 'all'
  | 'actionable'

type StatusOption = {
  value: ReviewNotificationListStatus
  label: string
  description: string
}

const defaultReviewNotificationStatus: ReviewNotificationListStatus = 'actionable'
const reviewNotificationListLimit = 100

export const reviewNotificationManagementStatusOptions: StatusOption[] = [
  {
    value: 'actionable',
    label: '尚未發送審核通知',
    description: '包含待發送、暫時失敗、發送失敗與 Webhook 未設定。',
  },
  {
    value: 'pending',
    label: '待發送',
    description: '已建立通知工作，但尚未成功送出。',
  },
  {
    value: 'retryable_error',
    label: '暫時失敗',
    description: 'n8n 或外部服務暫時失敗，可稍後重送。',
  },
  {
    value: 'failed',
    label: '發送失敗',
    description: '通知送出失敗，需管理員檢查後重送。',
  },
  {
    value: 'provider_not_configured',
    label: 'Webhook 未設定',
    description: '環境變數或 provider gate 尚未啟用。',
  },
  {
    value: 'sent',
    label: '已成功發送',
    description: 'n8n 已回覆成功，系統已標記完成。',
  },
  {
    value: 'all',
    label: '全部通知狀態',
    description: '查詢所有已建立的審核通知工作。',
  },
]

export default function ReviewNotificationsRoute() {
  const { user, isLoading } = useAuth()
  useBusinessWorkflowDataClientHydration(!isLoading)
  const canManageReviewNotifications = hasFormAdminAccess(user)
  const [statusFilter, setStatusFilter] = useState<ReviewNotificationListStatus>(
    defaultReviewNotificationStatus,
  )
  const [keyword, setKeyword] = useState('')
  const [jobs, setJobs] = useState<ReviewNotificationJobContract[]>([])
  const [isJobsLoading, setIsJobsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [processingJobId, setProcessingJobId] = useState<string | null>(null)

  const selectedStatusOption =
    reviewNotificationManagementStatusOptions.find((option) => option.value === statusFilter) ??
    reviewNotificationManagementStatusOptions[0]

  async function refreshJobs() {
    if (!canManageReviewNotifications) return
    setIsJobsLoading(true)
    setError('')
    try {
      const nextJobs = await listReviewNotificationJobs({
        status: statusFilter,
        limit: reviewNotificationListLimit,
      })
      setJobs(nextJobs)
    } catch (caught) {
      setError(formatReviewNotificationManagementError(caught))
    } finally {
      setIsJobsLoading(false)
    }
  }

  useEffect(() => {
    void refreshJobs()
  }, [canManageReviewNotifications, statusFilter])

  const filteredJobs = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase()
    if (!normalizedKeyword) return jobs
    return jobs.filter((job) =>
      [
        job.customer_short_name,
        job.form_type,
        job.submitted_by,
        job.salesManagerName,
        job.recipientEmail,
        job.lastErrorCode,
        job.message,
        job.providerDeliveryStatus,
      ]
        .join(' ')
        .toLocaleLowerCase()
        .includes(normalizedKeyword),
    )
  }, [jobs, keyword])

  async function handleRetry(jobId: string) {
    setProcessingJobId(jobId)
    setMessage('')
    setError('')
    try {
      await retryReviewNotificationJob(jobId)
      setMessage('已重新呼叫 n8n 發送審核通知，列表已更新。')
      await refreshJobs()
    } catch (caught) {
      setError(formatReviewNotificationManagementError(caught))
    } finally {
      setProcessingJobId(null)
    }
  }

  if (isLoading) {
    return <div className="grid min-h-dvh place-items-center text-sm text-slate-500">載入中...</div>
  }

  if (!canManageReviewNotifications) {
    return (
      <div className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center">
        <div>
          <h1 className="text-lg font-semibold text-slate-950">沒有審核通知管理權限</h1>
          <p className="mt-2 text-sm text-slate-500">
            此頁只開放管理員查詢未成功發送的審核通知，並手動再次發送。
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5 p-4 lg:p-6">
      <header className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold text-slate-950">審核通知管理</h1>
            <Badge variant="outline">n8n email</Badge>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            顯示填單後建立的審核通知記錄；n8n 沒有回覆成功時會留在預設清單，管理員可單筆再次發送。
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void refreshJobs()}
          disabled={isJobsLoading || Boolean(processingJobId)}
        >
          {isJobsLoading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <RotateCcw className="size-4" />
          )}
          重新整理
        </Button>
      </header>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="grid gap-3 lg:grid-cols-[minmax(220px,1.2fr)_minmax(220px,0.8fr)]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={keyword}
              onChange={(event) => setKeyword(event.currentTarget.value)}
              placeholder="搜尋客戶簡稱、填單人、主管、錯誤代碼"
              className="pl-9"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(value) => setStatusFilter(value as ReviewNotificationListStatus)}
          >
            <SelectTrigger>
              <SelectValue placeholder="通知狀態" />
            </SelectTrigger>
            <SelectContent>
              {reviewNotificationManagementStatusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
          <span>
            目前顯示 {filteredJobs.length} 筆，查詢條件：{selectedStatusOption.label}
          </span>
          <span>{selectedStatusOption.description}</span>
        </div>
      </section>

      {message ? (
        <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {message}
        </p>
      ) : null}
      {error ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="grid size-9 shrink-0 place-items-center rounded-md bg-cyan-50 text-cyan-700">
              <BellRing className="size-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-950">填單記錄通知狀態</h2>
              <p className="mt-1 text-xs text-slate-500">
                只有 n8n 回覆成功後才會標示已送出；非成功、逾時、格式不符或 provider
                未設定都會保留為可處理記錄。
              </p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>填單資料</TableHead>
                <TableHead>填單人</TableHead>
                <TableHead>通知收件者</TableHead>
                <TableHead>狀態</TableHead>
                <TableHead>嘗試</TableHead>
                <TableHead>時間</TableHead>
                <TableHead>操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">
                    目前沒有符合條件的審核通知記錄。
                  </TableCell>
                </TableRow>
              ) : (
                filteredJobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="min-w-[220px]">
                      <div className="font-medium text-slate-950">
                        {job.customer_short_name || '-'}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">{job.form_type || '-'}</div>
                    </TableCell>
                    <TableCell className="min-w-[160px]">{job.submitted_by || '-'}</TableCell>
                    <TableCell className="min-w-[240px]">
                      <div className="font-medium text-slate-900">{job.recipientEmail || '-'}</div>
                      <div className="mt-1 text-xs text-slate-500">
                        {job.salesManagerName || '-'}
                      </div>
                    </TableCell>
                    <TableCell className="min-w-[170px]">
                      <Badge variant={getReviewNotificationStatusVariant(job.status)}>
                        {getReviewNotificationStatusLabel(job.status)}
                      </Badge>
                      {job.lastErrorCode ? (
                        <div className="mt-1 text-xs text-slate-500">{job.lastErrorCode}</div>
                      ) : null}
                      {job.message ? (
                        <div className="mt-1 max-w-[240px] truncate text-xs text-slate-500">
                          {job.message}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>{job.attemptCount}</TableCell>
                    <TableCell className="min-w-[180px] text-xs text-slate-500">
                      <div>更新：{formatNotificationDateTime(job.updatedAt)}</div>
                      <div>排程：{formatNotificationDateTime(job.scheduledAt)}</div>
                      {job.sentAt ? <div>送出：{formatNotificationDateTime(job.sentAt)}</div> : null}
                    </TableCell>
                    <TableCell>
                      {job.status === 'sent' ? (
                        <Button type="button" variant="outline" size="sm" disabled>
                          已送出
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => void handleRetry(job.id)}
                          disabled={Boolean(processingJobId)}
                        >
                          {processingJobId === job.id ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <Send className="size-4" />
                          )}
                          再次發送
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  )
}

function getReviewNotificationStatusLabel(status: ReviewNotificationJobContract['status']) {
  const labels: Record<ReviewNotificationJobContract['status'], string> = {
    pending: '待發送',
    retryable_error: '暫時失敗',
    failed: '發送失敗',
    provider_not_configured: 'Webhook 未設定',
    sent: '已送出',
  }
  return labels[status]
}

function getReviewNotificationStatusVariant(status: ReviewNotificationJobContract['status']) {
  if (status === 'sent') return 'success'
  if (status === 'pending') return 'secondary'
  if (status === 'retryable_error' || status === 'provider_not_configured') return 'warning'
  return 'destructive'
}

function formatNotificationDateTime(value: string | null | undefined) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('zh-TW', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatReviewNotificationManagementError(error: unknown) {
  if (error instanceof Error && error.message.trim()) return error.message
  return '審核通知目前無法處理，請稍後再試。'
}
